import { FC } from 'react';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { createUseStyles } from 'react-jss';
import { IonItem, IonLabel, IonText } from '@ionic/react';
import { isPlatform } from '../../utils/isPlatform';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AddMedicationButton } from './AddMedicationButton';
import { Text } from '../../components/Text';
import { IMedication, IPatient } from '../../caremap/ICareMap';
import { DuoPrescriptionBottleIcon } from '../../theme/Icons';
import { PrivateCheckbox } from '../../components/PrivateCheckbox';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { PrivacyBadge } from '../../components/PrivacyBadge';
import { MedicationFormState } from './MedicationFormState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

const useStyles = createUseStyles({
    listWrapper: {
        paddingLeft: 16,
        marginTop: -4,

        '& > ion-item::part(native)': {
            paddingLeft: 0
        },

        '& ion-item > div[slot=start]': {
            marginInlineEnd: 1
        }
    },

    icon: {
        padding: '0px 8px 8px 0px'
    }
});

export type IMedicationList = {
    patient: IPatient;
    canAddEdit: boolean;
    medications?: IMedication[];
    onAdd: () => void;
    onClick: (id: string) => void;
};

export const MedicationList: FC<IMedicationList> = ({ medications, canAddEdit, patient, onClick, onAdd }) => {
    const classes = useStyles();
    const isDesktop = isPlatform('desktop');

    const mixpanel = useInstance(MixPanelEventEmitter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const medicationForm = useInstance(MedicationFormState);
    useSubscription(() => medicationForm);

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    if (!patient.medicationsIsPublic && isAnonymous) {
        return null;
    }

    const onAddHandler = (initialValues?: IMedication, usedPhoto?: boolean) => {
        if (initialValues) {
            medicationForm.setInitialValues(initialValues, usedPhoto);
        }
        onAdd();
    };

    return (
        <div data-type="medications-list">
            <DrawerSectionHeading text="Medications">
                {canAddEdit ? (
                    <AddMedicationButton
                        useIcon
                        onAdd={onAddHandler}
                    />
                ) : !patient.medicationsIsPublic && (
                    <div style={{marginRight: 8}}>
                        <PrivacyBadge data-type="medications-private-badge" />
                    </div>
                )}
            </DrawerSectionHeading>

            {canAddEdit && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: isDesktop ? -8 : -16}}>
                    <PrivateCheckbox
                        data-id="medication-visibility-checkbox"
                        checked={!patient.medicationsIsPublic}
                        onChange={isPrivate => {
                            patient.medicationsIsPublic = !isPrivate;
                            mixpanel.track('Medication Privacy Toggled', () => ({
                                'State': isPrivate ? 'private' : 'public'
                            }));
                            careMapState.updatePatient(patient);
                        }}
                    />
                    {!isDesktop && (
                        <AddMedicationButton
                            onAdd={onAddHandler}
                            forceManually // so the button says Add Manually
                        />
                    )}
                </div>
            )}

            <div className={classes.listWrapper}>
                {(!medications || medications.length === 0) && (
                    <IonText color="medium" style={{paddingLeft: 12}}>
                        No medications
                    </IonText>
                )}

                {medications?.map((medication, idx) => (
                    <IonItem
                        button
                        detail
                        key={medication.id}
                        onClick={() => onClick(medication.id!)}
                    >
                        <div slot="start" style={{/* marginTop: 10, marginBottom: 10 */}}>
                            <div className={classes.icon}><DuoPrescriptionBottleIcon color="medium" /></div>
                        </div>
                        <IonLabel>
                            <div>
                                <IonText data-type="name">{medication.name}</IonText>
                                <Text type="detail">{medication.instructions || <>&nbsp;</>}</Text>
                            </div>
                        </IonLabel>
                    </IonItem>
                ))}
            </div>
        </div>
    );
};
